import React, { useState, useEffect } from 'react';
import { StaticQuery, graphql } from 'gatsby';
import AniLink from 'gatsby-plugin-transition-link/AniLink';
import Swiper from 'react-id-swiper';
import { gsap } from 'gsap';

//Components
import Image from './image';
import useObserver from './useObserver';

const Slide = ({ slideData, lang }) => (
  <div className="swiper-slide">
    <article className="slider__slide px-md-7 pb-6 pb-md-7 pb-xl-10 pb-mg-15">
      <AniLink
        cover
        direction="up"
        bg="#092431"
        to={`/${lang}/processes/${slideData.title
          .toLowerCase()
          .replace(/ /g, '-')}`}
      >
        <Image
          className="mb-6"
          image={slideData.languages[lang].intro.media}
          alt={slideData.languages[lang].intro.title}
          loading="eager"
        />
      </AniLink>

      <h4 className="text-headline text-md-white mb-5">
        {slideData.languages[lang].intro.title}
      </h4>

      <p className="text-grey text-small">
        {slideData.languages[lang].intro.text}
      </p>
    </article>
  </div>
);

const Content = props => {
  const [swiper, setSwiper] = useState(null);
  const [ref, setRef, animate] = useObserver({
    init: true,
    root: null,
    rootMargin: `0% 0% 20% 0%`,
  });

  const translations = {
    en: {
      title: 'Sheet Metal Fabrication Processes',
      button: 'Processes overview',
    },
    de: {
      title: 'Blechherstellungsverfahren',
      button: 'Blechherstellungsverfahren',
    },
    bg: {
      title: 'Процеси',
      button: 'Преглед на процесите',
    },
  };

  const params = {
    getSwiper: setSwiper,
    loop: true,
    breakpoints: {
      0: {
        spaceBetween: 24,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 0,
      },
      1280: {
        slidesPerView: 3,
      },
      1680: {
        slidesPerView: 4,
      },
    },
  };

  const goToNextSlide = () => {
    if (swiper !== null) {
      swiper.slideNext();
    }
  };

  const goToPrevSlide = () => {
    if (swiper !== null) {
      swiper.slidePrev();
    }
  };

  useEffect(() => {
    if (ref) {
      gsap.set(ref, {
        opacity: 0,
        yPercent: 25,
        force3D: true,
        ease: 'power2.easeOut',
      });
    }
  }, [ref]);

  useEffect(() => {
    if (ref && animate) {
      gsap.to(ref, {
        duration: 0.525,
        delay: 0.6,
        opacity: 1,
        yPercent: 0,
        force3D: true,
        ease: 'power2.easeOut',
      });
    }
  }, [ref, animate]);

  return (
    <>
      <section
        ref={setRef}
        className="slider-processes pt-7 py-md-10 pt-lg-8 pt-xl-12 pt-mg-17 pl-5 pr-8 px-xl-10 px-mg-15"
      >
        <div className="container px-0">
          <div className="d-mg-flex align-items-center justify-content-between mb-8">
            <h2 className="text-mega mb-7 mb-mg-0">
              {translations[props.lang].title}
            </h2>

            <div className="d-none d-md-flex align-items-center justify-content-between justify-content-mg-end">
              <AniLink
                cover
                direction="up"
                bg="#092431"
                to={`/${props.lang}/processes`}
                className="btn btn-outline-primary rounded-lg mr-10"
              >
                <span className="text-black">
                  {translations[props.lang].button}
                </span>
              </AniLink>

              <div className="slider__controls">
                <button
                  onClick={goToPrevSlide}
                  className="btn btn-primary btn-rounded"
                >
                  <i className="far fa-long-arrow-left" aria-hidden="true"></i>
                </button>

                <button
                  onClick={goToNextSlide}
                  className="btn btn-primary btn-rounded ml-4"
                >
                  <i className="far fa-long-arrow-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Swiper {...params}>
          {props.slides
            .sort((a, b) => {
              if (a.node.parent === undefined || b.node.parent === undefined) {
                return 0;
              }

              if (a.node.parent.name < b.node.parent.name) {
                return -1;
              } else {
                return 1;
              }
            })
            .map(({ node }, i) => (
              <Slide
                index={i}
                key={node.title}
                slideData={node}
                lang={props.lang}
                animate={animate}
              />
            ))}
        </Swiper>
      </section>

      <div className="d-md-none mb-7">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between justify-content-mg-end">
            <AniLink
              cover
              direction="up"
              bg="#092431"
              to={`/${props.lang}/processes`}
              className="btn btn-outline-primary rounded-lg mr-10"
            >
              <span className="text-black">Processes</span>
            </AniLink>

            <div className="slider__controls">
              <button
                onClick={goToPrevSlide}
                className="btn btn-primary btn-rounded"
              >
                <i className="far fa-long-arrow-left" aria-hidden="true"></i>
              </button>

              <button
                onClick={goToNextSlide}
                className="btn btn-primary btn-rounded ml-4"
              >
                <i className="far fa-long-arrow-right" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SliderProcesses = ({ cmsPreview, lang }) => {
  if (cmsPreview) {
    return (
      <section className="py-8">
        <div className="container">
          <p className="text-title text-center">
            Processes slider will be generated based on the "Processes"
            collection
          </p>
        </div>
      </section>
    );
  }

  return (
    <StaticQuery
      query={graphql`
        query PROCESSES_LIST_QUERY {
          allProcessesJson {
            edges {
              node {
                title
                languages {
                  en {
                    intro {
                      title
                      media {
                        childImageSharp {
                          fluid(maxWidth: 336, maxHeight: 446) {
                            ...GatsbyImageSharpFluid_withWebp_noBase64
                          }
                        }
                        publicURL
                      }
                      text
                    }
                  }
                  de {
                    intro {
                      title
                      media {
                        childImageSharp {
                          fluid(maxWidth: 336, maxHeight: 446) {
                            ...GatsbyImageSharpFluid_withWebp_noBase64
                          }
                        }
                        publicURL
                      }
                      text
                    }
                  }
                  bg {
                    intro {
                      title
                      media {
                        childImageSharp {
                          fluid(maxWidth: 336, maxHeight: 446) {
                            ...GatsbyImageSharpFluid_withWebp_noBase64
                          }
                        }
                        publicURL
                      }
                      text
                    }
                  }
                }
                parent {
                  ... on File {
                    name
                  }
                }
              }
            }
          }
        }
      `}
      render={({ allProcessesJson: data }) => {
        if (!data || !data.edges.length) {
          return null;
        }

        return <Content slides={data.edges} lang={lang} />;
      }}
    />
  );
};

export default SliderProcesses;
